/**
 * TICKET-4 — EvidenceSlot
 *
 * Reusable evidence input component. Renders below each CP answer.
 * Three tabs: Text | URL | File
 *
 * Text: textarea, min 20 chars to count as evidence (char counter turns green at 20+)
 * URL:  input[type=url], must start with https://
 * File: drag-drop zone, max 10 MB; accepted MIME types listed in spec
 *
 * On save: calls PUT /api/accounts/:id/answers/:questionKey with evidence fields.
 * On save success: parent is notified via onSaved callback.
 *
 * Brand: ink #0B0B0F, red #DC2626, cream #FAFAF7 — DM Sans/Inter
 * No exclamation points, no emojis.
 */

import { useState, useRef, useCallback } from "react";

// ── Types ─────────────────────────────────────────────────────────────────────

export type EvidenceTabType = "text" | "url" | "file";

export interface EvidenceState {
  evidenceType: EvidenceTabType | null;
  evidence: string;
  evidenceUrl: string | null;
  evidenceFileName: string | null;
  evidenceFileSizeBytes: number | null;
}

interface EvidenceSlotProps {
  accountId: number;
  questionKey: string;
  selectedOption: string | null;
  /** Current evidence state (for pre-populating) */
  current?: {
    evidenceType: EvidenceTabType | null;
    evidence?: string;
    evidenceUrl?: string | null;
    evidenceFileName?: string | null;
  };
  /** Called after a successful save with updated evidence state */
  onSaved?: (state: EvidenceState & { isAssumption: boolean }) => void;
  /** Called when evidence is cleared */
  onCleared?: () => void;
  /** Auth token for API calls */
  authToken?: string;
  /** Whether the field is read-only (executive role etc.) */
  readOnly?: boolean;
}

const MAX_FILE_BYTES = 10 * 1024 * 1024; // 10 MB
const MIN_TEXT_CHARS = 20;

const ACCEPTED_MIME_TYPES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "image/png",
  "image/jpeg",
  "text/plain",
];

// ── Component ─────────────────────────────────────────────────────────────────

export default function EvidenceSlot({
  accountId,
  questionKey,
  selectedOption,
  current,
  onSaved,
  onCleared,
  authToken,
  readOnly = false,
}: EvidenceSlotProps) {
  const [tab, setTab] = useState<EvidenceTabType>(current?.evidenceType ?? "text");
  const [text, setText] = useState(current?.evidence ?? "");
  const [url, setUrl] = useState(current?.evidenceUrl ?? "");
  const [fileName, setFileName] = useState(current?.evidenceFileName ?? "");
  const [fileSizeBytes, setFileSizeBytes] = useState<number | null>(null);
  const [fileStoragePath, setFileStoragePath] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── Validation ──────────────────────────────────────────────────────────────

  const textValid = text.trim().length >= MIN_TEXT_CHARS;
  const urlValid = url.trim().startsWith("https://");
  const fileValid = !!fileName;

  const canSave =
    !readOnly &&
    !saving &&
    (tab === "text" ? textValid : tab === "url" ? urlValid : fileValid);

  // ── File handling ───────────────────────────────────────────────────────────

  function processFile(file: File) {
    setError(null);
    if (file.size > MAX_FILE_BYTES) {
      setError("File too large. Max 10MB.");
      return;
    }
    if (!ACCEPTED_MIME_TYPES.includes(file.type)) {
      setError("File type not accepted. Use PDF, DOCX, XLSX, PNG, JPG, or TXT.");
      return;
    }
    setFileName(file.name);
    setFileSizeBytes(file.size);
    // Note: actual upload to Supabase Storage is handled here.
    // For now we store the file reference locally until the upload endpoint
    // is implemented (TICKET-4-followup). The storage path is set after upload.
    setFileStoragePath(null);
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  }, []);

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  // ── Save ────────────────────────────────────────────────────────────────────

  async function handleSave() {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      const body: Record<string, unknown> = {
        questionKey,
        selectedOption,
        evidenceType: tab,
      };
      if (tab === "text") {
        body.evidence = text;
      } else if (tab === "url") {
        body.evidence = url;
        body.evidenceUrl = url;
        body.evidenceType = "url";
      } else if (tab === "file") {
        body.evidence = fileName;
        body.evidenceUrl = fileStoragePath;
        body.evidenceFileName = fileName;
        body.evidenceFileSizeBytes = fileSizeBytes;
      }

      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (authToken) headers["Authorization"] = `Bearer ${authToken}`;

      const res = await fetch(`/api/accounts/${accountId}/answers/${encodeURIComponent(questionKey)}`, {
        method: "PUT",
        headers,
        body: JSON.stringify(body),
      });

      if (res.status === 422) {
        const data = await res.json();
        setError(data.message ?? "Validation failed.");
        return;
      }
      if (!res.ok) {
        setError("Save failed. Please try again.");
        return;
      }

      const data = await res.json();
      setSuccess(true);
      onSaved?.({
        evidenceType: tab,
        evidence: text || url || fileName,
        evidenceUrl: tab === "url" ? url : fileStoragePath,
        evidenceFileName: tab === "file" ? fileName : null,
        evidenceFileSizeBytes: tab === "file" ? fileSizeBytes : null,
        isAssumption: data.isAssumption ?? false,
      });
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  function handleClear() {
    setText("");
    setUrl("");
    setFileName("");
    setFileSizeBytes(null);
    setFileStoragePath(null);
    setError(null);
    setSuccess(false);
    onCleared?.();
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="mt-2 rounded-lg border border-neutral-200 bg-[#FAFAF7] p-3 text-sm font-['Inter','DM_Sans',sans-serif]">
      {/* Tab bar */}
      <div className="flex gap-1 mb-2" role="tablist" aria-label="Evidence type">
        {(["text", "url", "file"] as EvidenceTabType[]).map((t) => (
          <button
            key={t}
            role="tab"
            aria-selected={tab === t}
            disabled={readOnly}
            onClick={() => { setTab(t); setError(null); setSuccess(false); }}
            className={[
              "px-2.5 py-1 rounded text-xs font-medium transition-colors",
              tab === t
                ? "bg-[#0B0B0F] text-[#FAFAF7]"
                : "bg-white text-neutral-500 border border-neutral-200 hover:bg-neutral-50",
              readOnly ? "opacity-50 cursor-not-allowed" : "cursor-pointer",
            ].join(" ")}
          >
            {t === "text" ? "Text" : t === "url" ? "URL" : "File"}
          </button>
        ))}
      </div>

      {/* Text tab */}
      {tab === "text" && (
        <div>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            disabled={readOnly}
            placeholder="Paste a quote, link, or contact name + date"
            rows={3}
            maxLength={5000}
            className="w-full resize-none rounded border border-neutral-200 bg-white px-2.5 py-1.5 text-xs text-[#0B0B0F] placeholder-neutral-400 focus:outline-none focus:ring-1 focus:ring-[#0B0B0F] disabled:opacity-50"
          />
          <div className={[
            "text-right text-[10px] mt-0.5 font-medium",
            textValid ? "text-emerald-600" : "text-neutral-400",
          ].join(" ")}>
            {text.trim().length}/{MIN_TEXT_CHARS} min
          </div>
          {text.trim().length > 0 && text.trim().length < MIN_TEXT_CHARS && (
            <p className="text-[10px] text-[#DC2626] mt-0.5">
              Evidence note must be at least 20 characters.
            </p>
          )}
        </div>
      )}

      {/* URL tab */}
      {tab === "url" && (
        <div>
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            disabled={readOnly}
            placeholder="https://..."
            className="w-full rounded border border-neutral-200 bg-white px-2.5 py-1.5 text-xs text-[#0B0B0F] placeholder-neutral-400 focus:outline-none focus:ring-1 focus:ring-[#0B0B0F] disabled:opacity-50"
          />
          {url && !urlValid && (
            <p className="text-[10px] text-[#DC2626] mt-0.5">
              Only HTTPS URLs are accepted.
            </p>
          )}
        </div>
      )}

      {/* File tab */}
      {tab === "file" && (
        <div>
          {fileName ? (
            <div className="flex items-center justify-between rounded border border-emerald-200 bg-emerald-50 px-2.5 py-2">
              <span className="text-xs text-emerald-800 font-medium truncate max-w-[200px]">{fileName}</span>
              {!readOnly && (
                <button
                  onClick={() => { setFileName(""); setFileSizeBytes(null); setFileStoragePath(null); }}
                  className="text-[10px] text-neutral-400 hover:text-[#DC2626] ml-2 shrink-0"
                >
                  Remove
                </button>
              )}
            </div>
          ) : (
            <div
              role="button"
              tabIndex={readOnly ? -1 : 0}
              aria-label="Drop file or click to upload"
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => !readOnly && fileInputRef.current?.click()}
              onKeyDown={(e) => e.key === "Enter" && !readOnly && fileInputRef.current?.click()}
              className={[
                "rounded border-2 border-dashed p-4 text-center cursor-pointer transition-colors",
                isDragging
                  ? "border-[#0B0B0F] bg-neutral-100"
                  : "border-neutral-300 hover:border-neutral-400 bg-white",
                readOnly ? "opacity-50 cursor-not-allowed" : "",
              ].join(" ")}
            >
              <p className="text-xs text-neutral-500">
                Drag and drop, or click to upload
              </p>
              <p className="text-[10px] text-neutral-400 mt-0.5">
                PDF, DOCX, XLSX, PNG, JPG, TXT — max 10 MB
              </p>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept=".pdf,.docx,.xlsx,.png,.jpg,.jpeg,.txt"
            onChange={handleFileChange}
          />
        </div>
      )}

      {/* Error / success feedback */}
      {error && (
        <p className="mt-1.5 text-[10px] text-[#DC2626]">{error}</p>
      )}
      {success && !error && (
        <p className="mt-1.5 text-[10px] text-emerald-600">Evidence saved.</p>
      )}

      {/* Action row */}
      {!readOnly && (
        <div className="mt-2 flex items-center justify-between">
          <button
            onClick={handleClear}
            className="text-[10px] text-neutral-400 hover:text-neutral-600"
          >
            Clear evidence
          </button>
          <button
            onClick={handleSave}
            disabled={!canSave}
            className={[
              "rounded px-3 py-1 text-xs font-medium transition-colors",
              canSave
                ? "bg-[#0B0B0F] text-[#FAFAF7] hover:bg-neutral-800"
                : "bg-neutral-200 text-neutral-400 cursor-not-allowed",
            ].join(" ")}
          >
            {saving ? "Saving..." : "Save evidence"}
          </button>
        </div>
      )}
    </div>
  );
}
